/**
 * Multi-vessel migration logic
 * Extracted from VesselContext to reduce file size and separate concerns
 */

import { devLog } from '../../utils/devLog';
import { legacyGet, legacySet, legacyRemove } from '../legacyUtils';

/**
 * List of keys that should be vessel-scoped.
 * These are cleaned up when a vessel is deleted to prevent orphaned data.
 *
 * IMPORTANT: When adding new vessel-scoped storage, add the key here.
 * Key names must match exactly what's used in useStorageState() calls.
 *
 * Last updated: 2026-01-25 - Added missing keys from codebase audit (MULTI-VESSEL-2026)
 */
const VESSEL_SCOPED_KEYS = [
  // =========================================================================
  // CREW & CERTIFICATES
  // =========================================================================
  'crew',                        // Crew member records (useCrewDomain)
  'crewMembers',                 // Legacy key - keep for migration
  'hours_of_rest',               // Work/rest hour records
  'certificates',                // Crew certificates
  'training_records',            // Training records
  'onboard_training',            // Onboard training sessions
  'familiarisation_records',     // Familiarisation progress
  'crew_familiarisations',       // Legacy familiarisation records
  'familiarisationSettings',     // Familiarisation config
  'hor_patterns',                // Hours of rest patterns
  'hor_signatures',              // Monthly HoR signatures

  // =========================================================================
  // EQUIPMENT & MAINTENANCE
  // =========================================================================
  'equipment_register',          // Equipment inventory (useEquipmentRegister)
  'equipment_documents',         // Equipment attached documents
  'equipmentChecks',             // Equipment check records
  'maintenance_schedule',        // Scheduled maintenance jobs
  'maintenance_history',         // Maintenance completion records
  'maintenanceRequirements',     // PMS job definitions
  'maintenance_settings',        // Maintenance module config

  // =========================================================================
  // DRILLS & TRAINING
  // =========================================================================
  'drills',                      // Drill completion records
  'drill_register',              // Drill requirements/schedule
  'drill_settings',              // Drill module config
  'customDrills',                // Custom drill definitions

  // =========================================================================
  // DEFECTS & INCIDENTS
  // =========================================================================
  'defects',                     // Defect/nonconformity records
  'incidents',                   // Incident reports (tailwind module)
  'incidentReports',             // Incident reports (legacy module)
  'investigations',              // Incident investigations
  'corrective_actions',          // Incident follow-up actions
  'riskAssessments',             // Risk assessment records

  // =========================================================================
  // VOYAGES & OPERATIONS
  // =========================================================================
  'voyages',                     // Voyage records
  'active_voyage',               // Current active voyage
  'voyage_settings',             // Voyage module config
  'voyage_custom_fields',        // Custom voyage fields
  'operations_voyages',          // Operations module voyages
  'operations_main_logbook',     // Main logbook entries
  'operations_checklists',       // Checklist completions
  'operations_checklist_templates', // Custom checklist templates
  'operations_permits',          // Permit records
  'operations_bunkering',        // Bunkering records
  'logbook_entries',             // Logbook entries (legacy)
  'daily_checks',                // Daily check records

  // =========================================================================
  // DOCUMENTS & SAFETY
  // =========================================================================
  'documents',                   // Document library
  'documentRegister',            // Document register
  'certificateTracking',         // Certificate tracking
  'emergency_customizations',    // Emergency panel customizations
  'customFamiliarisationItems',  // Custom familiarisation items
];

// Legacy keys that may exist in old installations - kept for migration only
const LEGACY_KEYS = [
  'crewMembers',                 // Now 'crew'
  'familiarisationRecords',      // Now 'familiarisation_records'
  'hoursOfRest',                 // Now 'hours_of_rest'
  'trainingRecords',             // Now 'training_records'
  'emergencyChecklists',         // Now 'emergency_customizations'
];

/**
 * Run migration from single-vessel to multi-vessel structure
 * Returns the list of vessels after migration
 *
 * FT-57 (2026-03-24): Removed "My Vessel" auto-creation.
 * New users now see the Vessel Gate Screen and must register a vessel with the server.
 * This prevents sync issues from client-only UUIDs.
 */
export function migrateToMultiVessel() {
  try {
    const existingVessels = legacyGet(null, 'vessels');

    if (!existingVessels) {
      devLog('No vessels found - new user will see vessel gate screen');

      // Clean up old vessel data first
      legacyRemove(null, 'vessel_config_basic');
      legacyRemove(null, 'vesselSettings');
      legacyRemove(null, 'vesselConfig');
      legacyRemove(null, 'fleet_vessels');

      // FT-57: No longer auto-create "My Vessel" - return empty array
      // User will see vessel gate screen and create vessel with server registration
      legacySet(null, 'vessels', []);

      devLog('Migration complete - vessels array initialized empty');
      return [];
    } else {
      // Clean up any field name inconsistencies
      let vessels = existingVessels;
      let needsUpdate = false;
      
      vessels = vessels.map(vessel => {
        const updated = { ...vessel };
        
        // Migrate vesselType to type
        if (vessel.vesselType && !vessel.type) {
          updated.type = vessel.vesselType;
          delete updated.vesselType;
          needsUpdate = true;
        }
        
        // Migrate vesselName to name
        if (vessel.vesselName && !vessel.name) {
          updated.name = vessel.vesselName;
          delete updated.vesselName;
          needsUpdate = true;
        }
        
        // Migrate legacy hasFixedFireSystem to space-specific fixedFFE
        if (vessel.hasFixedFireSystem && !vessel.fixedFFE) {
          // Legacy fixed system - assume engine space if diesel below deck
          updated.fixedFFE = {
            engine: vessel.engineLocation === 'below_deck' || vessel.enginePosition === 'below_deck',
            galley: false,
            accommodation: false
          };
          // Don't delete hasFixedFireSystem yet - keep for backward compatibility
          needsUpdate = true;
          devLog('Migrated hasFixedFireSystem to space-specific fixedFFE structure');
        }
        
        // Ensure fixedFFE structure exists for all vessels
        if (!updated.fixedFFE) {
          updated.fixedFFE = {
            engine: false,
            galley: false,
            accommodation: false
          };
          needsUpdate = true;
        }
        
        return updated;
      });
      
      if (needsUpdate) {
        legacySet(null, 'vessels', vessels);
        devLog('Migrated vessels to use standard field names (type, name)');
      }
      
      devLog(`Found ${vessels.length} vessel(s) in storage`);
      return vessels;
    }
  } catch (error) {

    return [];
  }
}

/**
 * Migrate crew data if it's in the wrong vessel ID
 */
export function migrateCrewData(currentVesselId) {
  // Get all localStorage keys
  const allKeys = Object.keys(localStorage);
  const crewKeys = allKeys.filter(k => k.startsWith('crewMembers_'));
  
  // If we have a current vessel but no crew for it, check for mismatched data
  if (currentVesselId && !legacyGet(currentVesselId, 'crewMembers')) {
    // Look for crew data in other keys
    for (const key of crewKeys) {
      const vesselId = key.replace('crewMembers_', '');
      const crewData = legacyGet(vesselId, 'crewMembers');
      if (crewData && crewData.length > 0) {
        // Found crew data in wrong key, migrate it
        devLog(`Migrating crew from ${key} to crewMembers_${currentVesselId}`);
        legacySet(currentVesselId, 'crewMembers', crewData);
        legacyRemove(vesselId, 'crewMembers');
        break; // Only migrate the first non-empty crew list found
      }
    }
  }
}

/**
 * Initialize vessel-scoped storage for a new vessel
 */
export function initializeVesselStorage(vesselId, vessel) {
  // Initialize empty data for vessel-scoped keys
  VESSEL_SCOPED_KEYS.forEach(key => {
    if (key === 'voyage_settings') return; // FT-219: Leave null so VoyageSettingsModal seeds correctly
    legacySet(vesselId, key, []);
  });
  
  // Initialize vessel settings with standard field names
  const vesselSettings = {
    name: vessel.name,
    imo: vessel.imo || '',
    type: vessel.type,
    category: vessel.category,
    flag: vessel.flag || 'GB',
    companyRequiredDrills: vessel.companyRequiredDrills || [],
    customDrills: vessel.customDrills || [],
    operatingStatus: vessel.operatingStatus || 'seagoing',
    hasPassengers: vessel.hasPassengers || false,
    maxPassengers: vessel.maxPassengers || 0,
    equipment: vessel.equipment || [],
    operations: vessel.operations || []
  };
  legacySet(vesselId, 'vesselSettings', vesselSettings);
}

/**
 * Clean up vessel-scoped storage when deleting a vessel.
 * Removes all data associated with the vessel to prevent orphaned data.
 */
export function cleanupVesselStorage(vesselId) {
  // Clean up all current vessel-scoped keys
  VESSEL_SCOPED_KEYS.forEach(key => {
    legacyRemove(vesselId, key);
  });

  // Also clean up legacy keys that may exist in old installations
  LEGACY_KEYS.forEach(key => {
    legacyRemove(vesselId, key);
  });

  // Clean up vessel settings
  legacyRemove(vesselId, 'vesselSettings');

  // Clean up date-scoped keys (e.g., daily_checks_VSL001-25_2025-01-25)
  try {
    const allKeys = Object.keys(localStorage);
    const vesselPrefix = `_${vesselId}`;
    allKeys
      .filter(k => k.includes(vesselPrefix))
      .forEach(k => {
        try {
          localStorage.removeItem(k);
        } catch (e) {
          // Ignore removal errors
        }
      });
  } catch (e) {
    // Ignore errors in cleanup
  }
}

export { VESSEL_SCOPED_KEYS };